import { BidService } from '../services/bid.service';

/**
 * Volume-capture geometry helpers. Ported from the mobile app
 * (mobile/src/utils/volumeCalc.js and the scene math in Geometry3DViewer.js) so web and
 * mobile agree on what a "volume" feature is and how its hull looks.
 *
 * Pure TypeScript: MUST NOT import @arcgis/core (this file runs in the ArcGIS-free karma scope).
 */

/** [lng, lat, alt?] */
export type LngLatAlt = number[];

export interface LocalPoint {
  x: number; // meters east of the centroid
  y: number; // meters above the lowest altitude (0 when altitude missing)
  z: number; // meters south of the centroid (three.js -z is north)
  alt: number | null;
}

export interface HullFace { a: number; b: number; c: number }

export interface Vec3 { x: number; y: number; z: number }

/** The parts of a report_bid_item GeoJSON feature these helpers read. */
export interface VolumeFeatureLike {
  geometry?: { type?: string; coordinates?: unknown } | null;
  properties?: {
    source?: {
      meta?: { geometry_capture_type?: string | null } | null;
      measurements?: { volume?: number; quantity?: number; num_points?: number } | null;
    } | null;
    modified_fields?: { quantity?: number; quantityMode?: string } | null;
  } | null;
}

/** The parts of a bid item the label formatter reads. */
export interface BidItemLike {
  uom?: string | null;
  unit?: string | null;
  quantity_rounding?: number | null;
  rollup_formula?: string | null;
}

export const VOLUME_MIN_POINTS = 4;
export const CUBIC_METERS_TO_CUBIC_YARDS = 1.30795;
/** Ground plane extends this × the hull bbox on each side (keep in sync with mobile). */
export const GROUND_PAD_FACTOR = 2;
export const MIN_GROUND_PAD_DEGREES = 0.002;

const METERS_PER_DEGREE_LAT = 111320;

export function isVolumeGeometry(
  geometry: { type?: string; coordinates?: unknown } | null | undefined,
  captureType?: string | null,
): boolean {
  if (captureType === 'volume') return true;
  return geometry?.type === 'MultiPoint'
    && Array.isArray(geometry.coordinates)
    && geometry.coordinates.length >= VOLUME_MIN_POINTS;
}

export function isVolumeFeature(feature: VolumeFeatureLike | null | undefined): boolean {
  return isVolumeGeometry(feature?.geometry, feature?.properties?.source?.meta?.geometry_capture_type ?? null);
}

/** Andrew's monotone chain. CCW order, no closing repeat. <3 points → input as-is. */
export function convexHull2D(coords: LngLatAlt[]): LngLatAlt[] {
  if (!Array.isArray(coords) || coords.length < 3) return coords || [];
  const pts = coords
    .filter(c => Array.isArray(c) && typeof c[0] === 'number' && typeof c[1] === 'number')
    .slice()
    .sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));
  if (pts.length < 3) return pts;
  const cross = (o: LngLatAlt, a: LngLatAlt, b: LngLatAlt) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: LngLatAlt[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: LngLatAlt[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

/** Closed [lng, lat] ring of the 2D hull — a GeoJSON Polygon ring for the map footprint. */
export function footprintRing(coords: LngLatAlt[]): number[][] {
  const hull = convexHull2D(coords).map(c => [c[0], c[1]]);
  if (hull.length === 0) return [];
  return [...hull, hull[0]];
}

/** Equirectangular ENU projection around the centroid, exactly as the mobile scene does. */
export function toLocalMeters(coords: LngLatAlt[]): {
  points: LocalPoint[];
  center: { lat: number; lng: number };
  minAlt: number;
  maxAlt: number;
  hasZData: boolean;
} {
  const valid = (coords || []).filter(c => Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1]));
  if (valid.length === 0) {
    return { points: [], center: { lat: 0, lng: 0 }, minAlt: 0, maxAlt: 0, hasZData: false };
  }
  const alts = valid.map(c => c[2]).filter(a => typeof a === 'number' && Number.isFinite(a)) as number[];
  const hasZData = alts.length >= VOLUME_MIN_POINTS && alts.length === valid.length;
  const minAlt = alts.length ? Math.min(...alts) : 0;
  const maxAlt = alts.length ? Math.max(...alts) : 0;
  const cLat = valid.reduce((s, c) => s + c[1], 0) / valid.length;
  const cLng = valid.reduce((s, c) => s + c[0], 0) / valid.length;
  const mLng = METERS_PER_DEGREE_LAT * Math.cos((cLat * Math.PI) / 180);
  const points = valid.map(c => {
    const alt = typeof c[2] === 'number' && Number.isFinite(c[2]) ? c[2] : null;
    return {
      x: (c[0] - cLng) * mLng,
      y: alt != null ? alt - minAlt : 0,
      z: -(c[1] - cLat) * METERS_PER_DEGREE_LAT,
      alt,
    };
  });
  return { points, center: { lat: cLat, lng: cLng }, minAlt, maxAlt, hasZData };
}

/** Incremental 3D convex hull. Faces are outward-facing triangles of point indices. */
export function convexHull3D(P: Vec3[]): HullFace[] {
  const n = P.length;
  if (n < 4) return [];
  const cross = (a: Vec3, b: Vec3, c: Vec3): Vec3 => ({
    x: (b.y - a.y) * (c.z - a.z) - (b.z - a.z) * (c.y - a.y),
    y: (b.z - a.z) * (c.x - a.x) - (b.x - a.x) * (c.z - a.z),
    z: (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x),
  });
  const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;
  const faceSign = (a: number, b: number, c: number, p: Vec3) => {
    const nrm = cross(P[a], P[b], P[c]);
    return dot(nrm, { x: p.x - P[a].x, y: p.y - P[a].y, z: p.z - P[a].z });
  };

  const i0 = 0;
  let i1 = 1, i2 = -1, i3 = -1;
  for (let i = 2; i < n; i++) {
    const cr = cross(P[i0], P[i1], P[i]);
    if (Math.abs(cr.x) > 1e-10 || Math.abs(cr.y) > 1e-10 || Math.abs(cr.z) > 1e-10) { i2 = i; break; }
  }
  if (i2 < 0) return [];
  for (let i = 0; i < n; i++) {
    if (i === i0 || i === i1 || i === i2) continue;
    if (Math.abs(faceSign(i0, i1, i2, P[i])) > 1e-10) { i3 = i; break; }
  }
  if (i3 < 0) return [];
  if (faceSign(i0, i1, i2, P[i3]) > 0) { const t = i1; i1 = i2; i2 = t; }

  let faces: HullFace[] = [
    { a: i0, b: i1, c: i2 }, { a: i0, b: i2, c: i3 }, { a: i0, b: i3, c: i1 }, { a: i1, b: i3, c: i2 },
  ];
  const used = new Set([i0, i1, i2, i3]);
  for (let i = 0; i < n; i++) {
    if (used.has(i)) continue;
    const p = P[i];
    const visible: HullFace[] = [];
    const invisible: HullFace[] = [];
    for (const f of faces) (faceSign(f.a, f.b, f.c, p) > 1e-10 ? visible : invisible).push(f);
    if (!visible.length) continue;
    const horizon: [number, number][] = [];
    for (const vf of visible) {
      for (const [ea, eb] of [[vf.a, vf.b], [vf.b, vf.c], [vf.c, vf.a]] as [number, number][]) {
        const shared = invisible.some(f =>
          (f.a === eb && f.b === ea) || (f.b === eb && f.c === ea) || (f.c === eb && f.a === ea));
        if (shared) horizon.push([ea, eb]);
      }
    }
    faces = invisible;
    for (const [ea, eb] of horizon) faces.push({ a: ea, b: eb, c: i });
    used.add(i);
  }
  return faces;
}

/**
 * Hull volume via the divergence theorem on the centered local-meter cloud.
 * Display fallback only — mobile's stored measurements.volume (projected CRS) is authoritative.
 */
export function computeVolumeCubicMeters(coords: LngLatAlt[]): {
  volume: number; hasZData: boolean; baseElevation: number; faces: number;
} {
  const local = toLocalMeters(coords);
  if (!local.hasZData || local.points.length < VOLUME_MIN_POINTS) {
    return { volume: 0, hasZData: false, baseElevation: local.minAlt, faces: 0 };
  }
  const cy = local.points.reduce((s, p) => s + p.y, 0) / local.points.length;
  const pts = local.points.map(p => ({ x: p.x, y: p.y - cy, z: p.z }));
  const faces = convexHull3D(pts);
  if (faces.length < 4) return { volume: 0, hasZData: true, baseElevation: local.minAlt, faces: 0 };
  let total = 0;
  for (const f of faces) {
    const a = pts[f.a], b = pts[f.b], c = pts[f.c];
    total += a.x * (b.y * c.z - b.z * c.y) + a.y * (b.z * c.x - b.x * c.z) + a.z * (b.x * c.y - b.y * c.x);
  }
  return { volume: Math.abs(total) / 6, hasZData: true, baseElevation: local.minAlt, faces: faces.length };
}

/** Padded lng/lat bbox for the ESRI ground tile (same padding rule as mobile). */
export function tileBbox(coords: LngLatAlt[]): { minLng: number; minLat: number; maxLng: number; maxLat: number } {
  const lngs = coords.map(c => c[0]);
  const lats = coords.map(c => c[1]);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const padLng = Math.max((maxLng - minLng) * GROUND_PAD_FACTOR, MIN_GROUND_PAD_DEGREES);
  const padLat = Math.max((maxLat - minLat) * GROUND_PAD_FACTOR, MIN_GROUND_PAD_DEGREES);
  return { minLng: minLng - padLng, minLat: minLat - padLat, maxLng: maxLng + padLng, maxLat: maxLat + padLat };
}

/** t in [0,1] (0 = lowest altitude) → RGB in [0,1]. Blue base → warm top, same ramp as mobile. */
export function elevationColor(t: number): [number, number, number] {
  const k = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
  return [(79 + k * 160) / 255, (195 - k * 130) / 255, (247 - k * 170) / 255];
}

/** "35.70 CY · 27.30 m³" | "35.70 CY" | "27.30 m³" | "No elevation data" */
export function formatVolumeLabel(feature: VolumeFeatureLike, bidItem: BidItemLike | null | undefined): string {
  const mode = feature?.properties?.modified_fields?.quantityMode || 'measured';
  const qty = BidService.calculateGpsFeatureQuantity(feature, mode, bidItem);
  const rounding = bidItem?.quantity_rounding ?? 2;
  const uom = bidItem?.uom || bidItem?.unit || '';
  const m3 = BidService.getVolumeCubicMeters(feature);
  const parts: string[] = [];
  if (qty) parts.push(uom ? `${qty.toFixed(rounding)} ${uom}` : qty.toFixed(rounding));
  if (m3 != null) parts.push(`${m3.toFixed(2)} m³`);
  return parts.length ? parts.join(' · ') : 'No elevation data';
}
